import { css } from 'styled-components';

// Memoized mapping of safe area directions
const SAFE_AREA_MAP = {
  'inline-start': 'safe-area-inset-left',
  'inline-end': 'safe-area-inset-right',
  'block-start': 'safe-area-inset-top',
  'block-end': 'safe-area-inset-bottom'
};

// Pre-compile padding properties for better performance
const PADDING_PROPERTIES = {
  'block-start': 'padding-block-start',
  'inline-end': 'padding-inline-end',
  'block-end': 'padding-block-end',
  'inline-start': 'padding-inline-start'
};

// Memoized function to get padding value
const getPaddingValue = (value, direction, useSafeArea) => {
  if (!useSafeArea) {
    return value;
  }
  return `max(${value}, env(${SAFE_AREA_MAP[direction]}))`;
};

// Optimized function to generate CSS rules
const createPaddingRule = (property, value) => `${property}: ${value};`;

// Helper function to generate logical padding CSS
const createLogicalPaddingMixin = (platform, useSafeArea = false) => css(["", ""], ({
  theme,
  $paddingInlineStart,
  $paddingInlineEnd,
  $paddingBlockStart,
  $paddingBlockEnd
}) => {
  const rules = [];
  if ($paddingInlineStart) {
    rules.push(createPaddingRule(PADDING_PROPERTIES['inline-start'], getPaddingValue(theme.spacing[$paddingInlineStart][platform], 'inline-start', useSafeArea)));
  }
  if ($paddingInlineEnd) {
    rules.push(createPaddingRule(PADDING_PROPERTIES['inline-end'], getPaddingValue(theme.spacing[$paddingInlineEnd][platform], 'inline-end', useSafeArea)));
  }
  if ($paddingBlockStart) {
    rules.push(createPaddingRule(PADDING_PROPERTIES['block-start'], getPaddingValue(theme.spacing[$paddingBlockStart][platform], 'block-start', useSafeArea)));
  }
  if ($paddingBlockEnd) {
    rules.push(createPaddingRule(PADDING_PROPERTIES['block-end'], getPaddingValue(theme.spacing[$paddingBlockEnd][platform], 'block-end', useSafeArea)));
  }
  return rules.join('\n');
});

// Helper function to generate padding CSS based on number of values
const createPaddingStyles = (splitPadding, theme, platform, useSafeArea) => {
  const themeValues = splitPadding.map(padding => {
    var _theme$spacing$paddin;
    return (_theme$spacing$paddin = theme.spacing[padding]) === null || _theme$spacing$paddin === void 0 ? void 0 : _theme$spacing$paddin[platform];
  });
  const createPaddingValues = indices => indices.map((index, i) => getPaddingValue(themeValues[index], Object.keys(PADDING_PROPERTIES)[i], useSafeArea));
  const paddingMaps = {
    1: () => {
      const [value] = createPaddingValues([0, 0, 0, 0]);
      return Object.values(PADDING_PROPERTIES).map(prop => createPaddingRule(prop, value));
    },
    2: () => {
      const [block, inline] = createPaddingValues([0, 1]);
      return [createPaddingRule(PADDING_PROPERTIES['block-start'], block), createPaddingRule(PADDING_PROPERTIES['inline-end'], inline), createPaddingRule(PADDING_PROPERTIES['block-end'], block), createPaddingRule(PADDING_PROPERTIES['inline-start'], inline)];
    },
    3: () => {
      const values = createPaddingValues([0, 1, 2, 1]);
      return Object.values(PADDING_PROPERTIES).map((prop, i) => createPaddingRule(prop, values[i]));
    },
    4: () => {
      const values = createPaddingValues([0, 1, 2, 3]);
      return Object.values(PADDING_PROPERTIES).map((prop, i) => createPaddingRule(prop, values[i]));
    }
  };
  return css(["", ""], (paddingMaps[splitPadding.length] || paddingMaps[4])().join('\n'));
};

// Create padding mixin factory with memoization
const createPaddingMixin = (() => {
  const cache = new Map();
  return (platform, useSafeArea = false) => {
    const cacheKey = `${platform}-${useSafeArea}`;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    const mixin = /*#__PURE__*/css(["", ""], ({
      $padding,
      theme
    }) => {
      if (!$padding) {
        return createLogicalPaddingMixin(platform, useSafeArea);
      }
      const splitPadding = $padding.trim().split(' ');
      return css(["", " ", ""], createPaddingStyles(splitPadding, theme, platform, useSafeArea), createLogicalPaddingMixin(platform, useSafeArea));
    });
    cache.set(cacheKey, mixin);
    return mixin;
  };
})();
const paddingMixin = createPaddingMixin('default', false);
const paddingMixinWithSafeArea = createPaddingMixin('default', true);
const mobilePaddingMixin = createPaddingMixin('mobile', false);
const mobilePaddingMixinWithSafeArea = createPaddingMixin('mobile', true);

export { paddingMixinWithSafeArea as a, mobilePaddingMixinWithSafeArea as b, mobilePaddingMixin as m, paddingMixin as p };
//# sourceMappingURL=index-BPYuRWh5.js.map
